import { ProviderNames } from "models/Providers";
import { orZero } from "utils/NumUtils";
import { pickProps } from "utils/ObjectUtils";
import { Account } from "./Account";

// TODO: Separate these, and move/combine with backend api CampaignObjects.ts

export const enum AdObjectType {
    Campaign = 'campaign',
    Adset = 'adset',
    Ad = 'ad'
}

export const enum AdObjectStatus {
    Active = 'ACTIVE',
    Inactive = 'INACTIVE',
    Paused = 'PAUSED',
    Archived = 'ARCHIVED',
    Deleted = 'DELETED',
    Error = 'ERROR',
    Unknown = 'UNKNOWN'
}

/**
 * @description Encapsulates campaign data for a given day, grouped by provider, and campaign, adset, ad.
 * @class CampaignData
 */
export class DailyCampaignData {

    public dayStart: string;

    public dayEnd: string;

    public campaigns: Array<Campaign>;

    public totals: Totals;

    public otherTotals: Totals; // hold the "static" totals (added on top of campaign data)

    // hold totals of just the campaigns // ignore for now
    //public campaignTotals: Totals;

    constructor(dayStart?, dayEnd?) {
        this.dayStart = dayStart;
        this.dayEnd = dayEnd || dayStart;
        this.campaigns = [];
        this.totals = new Totals();
    }

    // This first finds all campaigns matching by id or name. Then from those, returns one
    // if it finds a match by given provider name, or otherwise whichever other it finds.
    // If it finds a campaign without a provider, it will set it to the given provider.
    // If none exist, a new campaign is created.
    getCampaign = (cId, cName, providerName) => {
        let campaign;

        // filter by id and name first, then figure out provider later
        let campaigns = this.campaigns.filter(_c => {
            if (!cId && !_c.id) return true; // unknown campaign
            if (cId && _c.id && cId == _c.id) return true;
            if (cName && _c.name && cName == _c.name) return true;
            return false;
        });

        if (campaigns.length == 1) {
            campaign = campaigns[0];
        } else if (campaigns.length) {
            if (providerName) {
                let providerCampaign = campaigns.find(_c => _c.provider == providerName);
                if (providerCampaign) {
                    campaign = providerCampaign;
                } else {
                    console.log("WARNING: Matching campaigns found, but not by given provider. Taking first.", providerName, campaigns);
                    campaign = campaigns[0];
                    if (!campaign.provider) {
                        console.log(`WARNING: Campaign has no provider, setting to new:`, providerName);
                        campaign.provider = providerName;
                    }
                }
            } else {
                campaign = campaigns[0];
                console.log(`WARNING: No provider given, taking first campaign matching ID or name`, campaign, campaign.provider);
            }
        }

        if (!campaign) {
            campaign = new Campaign(cId, cName, providerName);
            this.addCampaign(campaign);
        }

        // now... we do this magic of making sure the campaign has an ID, because the adaya provider data will only give the utm_campaign (either id or name)
        // and it sets it to both first, possibly, then later FB provider data sees it, uses it, but it doesn't have the ID yet, so we set it here.
        if (campaign.id == campaign.name) {
            // it could be undefined
            if (cId != cName) {
                campaign.id = cId;
                //console.log(`NOTE: overring campaign ID with new provider:`, providerName, campaign);
            }
        }

        return campaign;
    }

    // recalculated data each time
    getData = (pickData?) => {
        this.recalculateTotals();
        return {
            campaigns: this.campaigns.map(c => c.getData(pickData)),
            totals: this.totals
        }
    }

    /**
     * @description Calculates current set of campaign totals on the fly.
     * This recalculates the totals each time it's called.
     */
    getTotals = () => {
        // calls .recalculate() first
        return this.totals.getData();
    }

    // just adds the campaign, even if it exists (convenience for making new data set and retaining children and totals).
    addCampaign(c: Campaign) {
        this.campaigns.push(c);
        this.recalculateTotals();
    }

    // convenience method to add to 'static' totals (can also add directly)
    addTotals(totals, dontAdd?) {
        if (typeof totals != 'object') return;
        if (!this.otherTotals) this.otherTotals = new Totals();
        this.otherTotals.addData(totals, dontAdd);
        this.recalculateTotals(); // recalculate after change
    }

    // resets totals and sum campaigns, on top of the static totals as well.
    recalculateTotals() {
        // start with new base
        this.totals = new Totals();

        //this.campaignTotals = new Totals();

        // add static totals (used for non-campaign-based data)
        if (this.otherTotals) this.totals.addData(this.otherTotals);

        // add campaign totals
        this.campaigns.forEach(c => {
            //this.campaignTotals.addData(c.totals);
            this.totals.addData(c.totals);
        });

        // recalculate the adset and ad totals?
        // this.campaigns.forEach(campaign => {
        //     c.adsets.forEach(adset => {
        //         adset.ads.forEach(ad => {
        //             ad.getTotals();
        //         });
        //         adset.getTotals();
        //     });
        //     totals.addData(campaign.totals);
        // });
    }

}

class AdObject {
    id;
    name;
    provider: ProviderNames;
    status: AdObjectStatus;
    totals: Totals;

    constructor(id, name, providerName) {
        this.id = id;
        this.name = name;
        this.provider = providerName;
        this.status = AdObjectStatus.Unknown;
        this.totals = new Totals();
    }

}

export class Campaign extends AdObject {
    adsets: Adset[];

    constructor(id, name, providerName) {
        super(id, name, providerName);
        this.adsets = [];
    }

    getAdset(aId, aName, providerName?) {
        //console.log(`getAdset`, aId, aName)
        let adset = this.adsets.find(_a => {
            if (!aId && !_a.id) return true; // unknown adset
            if (aId && _a.id && aId == _a.id) return true;
            if (aName && _a.name && aName == _a.name) return true;
            return false;
        });

        if (!adset) {
            adset = new Adset(aId, aName, providerName);
            this.adsets.push(adset);
        }
        return adset;
    }

    // just adds the ad, even if it exists (convenience for making new data set and retaining children and totals).
    addAdset(a: Adset) {
        this.adsets.push(a);
    }

    getData(pickData) {
        return {
            id: this.id,
            name: this.name,
            provider: this.provider,
            totals: this.totals.getData(pickData),
            adsets: this.adsets.map(a => a.getData(pickData))
        }
    }
}

export class Adset extends AdObject {
    ads: Ad[];

    constructor(id, name, providerName?) {
        super(id, name, providerName);
        this.ads = [];
    }

    getAd(aId, aName, providerName?) {
        let ad = this.ads.find(_a => {
            if (!aId && !_a.id) return true; // unknown adset
            if (aId && _a.id && aId == _a.id) return true;
            if (aName && _a.name && aName == _a.name) return true;
            return false;
        });

        if (!ad) {
            ad = new Ad(aId, aName, providerName);
            this.ads.push(ad);
        }
        return ad;
    }

    // just adds the ad, even if it exists (convenience for making new data set and retaining children and totals).
    addAd(a: Ad) {
        this.ads.push(a);
    }

    getData(pickData?) {
        return {
            id: this.id,
            name: this.name,
            totals: this.totals.getData(pickData),
            ads: this.ads.map(a => a.getData(pickData))
        }
    }
}

export class Ad extends AdObject {

    constructor(id, name, providerName?) {
        super(id, name, providerName);
    }

    getData(pickData?) {
        return {
            id: this.id,
            name: this.name,
            totals: this.totals.getData(pickData)
        }
    }
}

export class Totals {
    sales = 0;
    adspend = 0;
    orders = 0;
    revenue = 0;
    items = 0;
    pageviews = 0;
    sessions = 0;
    addToCarts = 0;
    checkouts = 0;

    // calculated stats:
    cpp = 0;    // CPP = adspend / orders
    aov = 0;    // AOV = revenue / orders
    mer = 0;    // MER = Shopify sales / adspend
    roas = 0;   // ROAS = Adaya revenue / adspend
    profit = 0;

    scores = new ScoreTotals();

    // supports omitting some properties, usedful for removing ie. provider revenue.
    addData(o, dontAdd?) {
        if (!o) return;

        const _tryAddProp = (prop) => {
            try {
                if (dontAdd?.length && dontAdd.includes(prop)) return;

                if (o[prop] && typeof o[prop] != 'object') {
                    if (typeof this[prop] == 'undefined') {
                        //console.warn("Warning: property is not supported for adding to Totals: " + prop);
                        return;
                    }
                    this[prop] += orZero(o[prop]);
                }
            } catch (e) {
                console.warn(`Warning: error adding property to Totals for prop '${prop}'`, e);
            }
        }

        // tries to add only "legal" properties (could just do a single line prop lookup?)
        Object.keys(o).forEach(p => {
            _tryAddProp(p);
        });

        // _tryAddProp('sales');
        // _tryAddProp('adspend');
        // _tryAddProp('revenue');
        // _tryAddProp('items');
        // _tryAddProp('orders');
        // _tryAddProp('pageviews');
        // _tryAddProp('sessions');
        // _tryAddProp('addToCarts');
        // _tryAddProp('checkouts');

        this.recalculateTotals();
    }

    getData(pickData?) {
        this.recalculateTotals();

        let t = {
            sales: this.sales,
            adspend: this.adspend,
            orders: this.orders,
            revenue: this.revenue,
            items: this.items,
            pageviews: this.pageviews,
            sessions: this.sessions,
            addToCarts: this.addToCarts,
            checkouts: this.checkouts,

            cpp: this.cpp,
            aov: this.aov,
            mer: this.mer,
            roas: this.roas,
            profit: this.profit,

            scores: this.scores
        }

        if (pickData && Array.isArray(pickData)) {
            return pickProps(t, pickData);
        }

        return t;
    }

    recalculateTotals() {
        // calculate these on the fly upon any change (so we don't have to do it later):
        this.cpp = orZero(this.adspend / this.orders);
        this.aov = orZero(this.revenue / this.orders);
        this.mer = orZero(this.sales / this.adspend);
        this.roas = orZero(this.revenue / this.adspend);
        this.profit = orZero(this.revenue - this.adspend);
        // some views (e.g. Ads Manager footer total) read `spend`; keep it in sync with adspend.
        this.spend = this.adspend;

        this.recalculateScores();
    }

    recalculateScores() {
        if (!this.scores) this.scores = new ScoreTotals();
        this.scores.itemsPerCustomer = orZero(this.items / this.orders);
        this.scores.conversionRate = orZero((this.orders / this.sessions) * 100);
        this.scores.addToCartConversionRate = orZero(this.items / this.addToCarts) * 100;
    }
}

// extended totals add onto basic totals for campaign calculations
export class ScoreTotals {
    conversionRate = 0;
    addToCartConversionRate = 0;
    itemsPerCustomer = 0;

    conversionIndex = 0;
    addToCartConversionIndex = 0;
    itemsPerCustomerIndex = 0;

    score = 0;
}

// For later: make features to merge data sets and do calculations/keep in memory...
// export class DailyCampaignDataSet {

//     private dayStart: string;

//     private dayEnd: string;

//     public campaigns: Array<Campaign>;

//     public totals: Totals;

//     public addCampaignDataSet(cd) {
//     }
// }

export const getDefaultEnsureCampaignDataOptions = (opts?: {}): EnsureCampaignDataOptions => {
    let defaults = {
        retryFailedRequest: true,
        forceUpdate: false,
        suppressErrors: false,
        retryOnApiLimit: false,
        retryWaitLong: false,
        reverseDays: false,
        dontFetchNewData: false,
        clearDataBefore: false,
        resolveUnknownObjects: false,
        onlyPaidsocial: true,
    };
    let props = Object.keys(defaults);
    opts = Object.assign(defaults, opts || {});
    return pickProps(opts, props);
}

export type EnsureCampaignDataOptions = {

    // Retry the request if any providers returns with an error.
    retryFailedRequest?: boolean;

    // Force the providers to fetch new data for the given request, regardless if it's already stored locally.
    forceUpdate?: boolean;

    // Suppress all errors and allow all providers to fulfill, returning results at the end.
    // Otherwise, if any provider throws an error, the whole request will.
    suppressErrors?: boolean;

    // Wait RETRY_WAIT_SECONDS_ON_API_LIMITS between retrying a failed provider.
    retryOnApiLimit?: boolean;

    // Wait ~30s on an api limit error, instead of normal ~2s.
    retryWaitLong?: boolean;

    // Fulfill the request days in reverse.
    reverseDays?: boolean;

    // Tells the request or job to not ask providers for new data, just return what local data it has in db.
    dontFetchNewData?: boolean;

    // If true, the system will delete the current provider data in mongo, for the current day, before checking and refetching it.
    clearDataBefore?: boolean;

    // If true, the final step of the request will try to resolve unknown ad object IDs to their names/real objects.
    resolveUnknownObjects?: boolean;

    // Include only paidsocial-attributed event data in the response, default=false
    onlyPaidsocial?: boolean;

    // This is here to be able to pass request params back to handlers
    params?: any;

}

export type DataFilter = {

    account?: Account;

    timeRange: {
        from: string;
        to?: string;
    }

    providers?: Array<string>;

    onlyPaidsocial?: boolean;

}

// backwards compatibility for now
export type CampaignDataFilter = DataFiler & {
}